import React, { useEffect, useState } from 'react'
import CmdKGroup from '../CmdKGroup'
import CommandItem from '../CmdKItem'
import { MenuBookRounded } from '@mui/icons-material'
import { useLocation } from '@cedarjs/router'
import { useOrgName } from 'src/hooks/useOrgName'
import useR2R from 'src/hooks/requests/useR2R'
import { useDebounce } from 'usehooks-ts'
import useModal from 'src/hooks/useModal'

interface KnowledgeBaseModuleProps {
  search: string
  onClose: () => void
  pages: CommandMenuGroup[][]
}

const KnowledgeBaseModule: React.FC<KnowledgeBaseModuleProps> = ({
  search,
  onClose,
  pages,
}) => {
  const { client: r2rClient } = useR2R()
  const debouncedSearch = useDebounce(search, 500)
  const shouldSearch = debouncedSearch && debouncedSearch.length >= 3
  const [results, setResults] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const { handleOpen: handleOpenIframe } = useModal('iframe')

  const topLevel = (pages?.length ?? 0) === 0

  useEffect(() => {
    let isMounted = true

    const searchKnowledgeBase = async () => {
      if (!shouldSearch) return

      setIsLoading(true)
      setResults([])
      try {
        const results = await r2rClient.retrieval.search({
          query: debouncedSearch,
          searchMode: 'basic',
          searchSettings: {
            filters: {
              collectionIds: {
                $eq: process.env.CLUESO_KNOWLEDGEBASE_COLLECTION_ID,
              },
            },
            graphSearchSettings: {
              useGraphSearch: false,
            },
          },
        })

        if (isMounted) {
          setResults(
            results.results.chunkSearchResults
              .filter((result) => {
                return result.score > 0.3
              })
              .map((result) => {
                return {
                  title: result.metadata.title,
                  url: result.metadata.sourceUrl,
                }
              })
          )
        }
      } catch (error) {
        console.error('Error searching knowledge base:', error)
        if (isMounted) {
          setResults([])
        }
      } finally {
        if (isMounted) {
          setIsLoading(false)
        }
      }
    }

    searchKnowledgeBase()

    return () => {
      isMounted = false
    }
  }, [debouncedSearch, shouldSearch, r2rClient])

  useEffect(() => {
    if (!search) {
      setResults([])
    }
  }, [search])

  if (!topLevel) {
    return null
  }

  return (
    <CmdKGroup heading="Knowledge Base">
      {!search && (
        <CommandItem
          disabled
          startIcon={<MenuBookRounded />}
          subtext="Knowledgebase"
        >
          Start typing to search knowledge base...
        </CommandItem>
      )}
      {search && !shouldSearch && (
        <CommandItem
          disabled
          startIcon={<MenuBookRounded />}
          subtext="Type at least 3 characters to search"
        >
          Keep typing...
        </CommandItem>
      )}
      {isLoading && (
        <CommandItem
          disabled
          startIcon={<MenuBookRounded />}
          subtext="Knowledgebase"
        >
          Searching for &quot;{debouncedSearch}&quot;
        </CommandItem>
      )}
      {!isLoading && shouldSearch && results.length === 0 && (
        <CommandItem
          disabled
          startIcon={<MenuBookRounded />}
          subtext="Knowledgebase"
        >
          No matches for &quot;{debouncedSearch}&quot;
        </CommandItem>
      )}
      {results.map((result) => (
        <CommandItem
          key={result.url}
          startIcon={<MenuBookRounded />}
          subtext="Knowledgebase"
          onSelect={() => {
            onClose()
            handleOpenIframe({
              sourceUrl: result.url,
              title: `Knowledge Base: ${result.title}`,
            })
          }}
        >
          {result.title}
        </CommandItem>
      ))}
    </CmdKGroup>
  )
}

export default KnowledgeBaseModule
